/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import HeroBackground from '@/components/storyblok/components/kits/HeroBackground'
import { AssetStoryblok, KitOverviewBentoGridStoryblok } from '@/lib/storyblok/types/generated-types'
import { cn } from '@/utils/css'
import { motion, useScroll, useTransform } from 'framer-motion'
import Image from 'next/image'
import React, { useRef } from 'react'

/** A description of the available properties for this component. */
type BentoImageTextGridProps = {
  /** The Storyblok blok that represents this component. */
  blok: KitOverviewBentoGridStoryblok
}

type GridItemDefinition = {
  id: number
  type: 'text' | 'image'
  contentKey: keyof Omit<KitOverviewBentoGridStoryblok, '_uid' | 'component'>
  bgColor?: string
  mobileSpan: number
  minHeight?: string
  desktopClasses: string
}

const gridItemDefinitions: GridItemDefinition[] = [
  {
    id: 1,
    type: 'text',
    contentKey: 'text_one',
    bgColor: 'bg-primary-500 text-white',
    mobileSpan: 1,
    minHeight: 'min-h-28 md:min-h-44',
    desktopClasses: 'md:block md:col-start-1 md:row-start-2 md:col-span-1 md:row-span-1',
  },
  {
    id: 2,
    type: 'image',
    contentKey: 'image_one',
    mobileSpan: 1,
    minHeight: 'min-h-28 md:min-h-44',
    desktopClasses: 'md:block md:col-start-1 md:row-start-1 md:col-span-1 md:row-span-1',
  },
  {
    id: 3,
    type: 'image',
    contentKey: 'image_two',
    mobileSpan: 2,
    minHeight: 'min-h-28 md:min-h-44',
    desktopClasses: 'md:block md:col-start-2 md:row-start-1 md:col-span-1 md:row-span-3',
  },
  {
    id: 4,
    type: 'image',
    contentKey: 'image_three',
    mobileSpan: 1,
    minHeight: 'min-h-28 md:min-h-44',
    desktopClasses: 'md:block md:col-start-3 md:row-start-2 md:col-span-1 md:row-span-2',
  },
  {
    id: 5,
    type: 'text',
    contentKey: 'text_two',
    bgColor: 'bg-secondary-500 text-white',
    mobileSpan: 1,
    minHeight: 'min-h-28 md:min-h-44',
    desktopClasses: 'md:block md:col-start-1 md:row-start-3 md:col-span-1 md:row-span-1',
  },
  {
    id: 6,
    type: 'text',
    contentKey: 'text_three',
    bgColor: 'bg-primary-300 text-white',
    mobileSpan: 2,
    minHeight: 'min-h-28 md:min-h-44',
    desktopClasses: 'md:block md:col-start-3 md:row-start-1 md:col-span-1 md:row-span-1',
  },
]

const animationTargets = [
  { x: '-50vw', y: '-50vh' },
  { x: '+50vw', y: '-50vh' },
  { x: '0vw', y: '-50vh' },
  { x: '-55vw', y: '+10vh' },
  { x: '+55vw', y: '+10vh' },
  { x: '0vw', y: '+50vh' },
]

const BentoImageTextGrid: React.FC<BentoImageTextGridProps> = ({ blok }) => {
  const targetRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start start', 'end end'] })
  const animEndProgress = 150 / 275
  const transformProgress = useTransform(scrollYProgress, [0, animEndProgress], [0, 1])
  const itemOpacity = useTransform(scrollYProgress, [0, 0.1, animEndProgress], [1, 1, 0])
  const contentOpacity = useTransform(scrollYProgress, [0, 0.25, animEndProgress], [0, 0, 1])

  return (
    <div ref={targetRef} className={cn('relative w-full', 'h-[275vh]')}>
      <div
        className={cn(
          'sticky w-full overflow-hidden',
          `top-[115px] md:top-[130px]`,
          `h-[calc(100vh-115px)] md:h-[calc(100vh-130px)]`,
        )}
      >
        <motion.div
          id={'revealed-content'}
          style={{ opacity: contentOpacity }}
          className='absolute inset-0 z-10 flex items-center justify-center'
        >
          <HeroBackground />
        </motion.div>

        <motion.div
          id={'bento-grid'}
          className={cn('absolute inset-0 z-20 flex items-center justify-center pointer-events-none')}
        >
          <div
            className={cn(
              'grid p-4 gap-4',
              'grid-cols-2', // Mobile default
              'md:grid-cols-3 md:grid-rows-3', // Desktop structure
              'w-full max-w-7xl',
            )}
          >
            {gridItemDefinitions.map((item, index) => {
              const target = animationTargets[index] || { x: '0vw', y: '0vh' }
              const x = useTransform(transformProgress, [0, 1], ['0vw', target.x])
              const y = useTransform(transformProgress, [0, 1], ['0vh', target.y])
              const scale = useTransform(transformProgress, [0, 1], [1, 1.2])
              const itemContent = blok[item.contentKey]
              const textContent = typeof itemContent === 'string' ? itemContent : undefined
              const imageContent =
                typeof itemContent === 'object' && itemContent !== null && 'filename' in itemContent
                  ? (itemContent as AssetStoryblok)
                  : undefined

              return (
                <motion.div
                  key={item.id}
                  style={{ opacity: itemOpacity, translateX: x, translateY: y, scale: scale }}
                  className={cn(
                    'rounded-2xl relative pointer-events-auto',
                    `col-span-${item.mobileSpan}`,
                    item.desktopClasses,
                    item.minHeight,
                    item.type === 'image' && 'overflow-hidden',
                    item.bgColor,
                    'block',
                  )}
                >
                  {item.type === 'text' && textContent && (
                    <div className='p-4 md:p-6 font-semibold text-xl h-full flex items-center justify-center text-center overflow-hidden'>
                      <span>{textContent}</span>
                    </div>
                  )}
                  {item.type === 'image' && imageContent?.filename && (
                    <Image
                      src={imageContent.filename}
                      alt={imageContent.alt ?? `Image ${item.id}`}
                      fill
                      className={'object-cover'}
                      priority={index < 3}
                      sizes='(max-width: 768px) 50vw, 33vw'
                    />
                  )}
                </motion.div>
              )
            })}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export { BentoImageTextGrid }
